// Phase progress tracker + Case File live panel.
import { Building2, CheckCircle2, AlertTriangle, IndianRupee, FileText, XCircle, Lock } from "lucide-react";

const PHASES = [
  { id: "WELCOME", label: "Welcome" },
  { id: "STRUCTURE_GUIDANCE", label: "Structure" },
  { id: "BASICS", label: "Basics" },
  { id: "NAMES", label: "Names" },
  { id: "STAKEHOLDERS", label: "Stakeholders" },
  { id: "COMPANY_DETAILS", label: "Office" },
  { id: "OBJECTS_DRAFTING", label: "Objects" },
  { id: "REVIEW", label: "Review" },
  { id: "DONE", label: "Done" },
];

const PRECHECK_STYLES = {
  LOOKS_AVAILABLE: "bg-emerald-100 text-emerald-800",
  RISKY: "bg-amber-100 text-amber-900",
  LIKELY_REJECTED: "bg-red-100 text-red-800",
  NOT_CHECKED: "bg-slate-100 text-slate-600",
};

function maskAadhaar(last4) {
  return last4 ? `•••• •••• ${last4}` : "—";
}

export default function CaseFilePanel({ caseData, onPay, paymentUrl }) {
  if (!caseData) return null;
  const phase = caseData.conversation_state?.current_phase || "WELCOME";
  const phaseIdx = PHASES.findIndex((p) => p.id === phase);
  const company = caseData.company || {};
  const names = company.name_preferences || [];
  const stakeholders = caseData.stakeholders || [];
  const validation = caseData.validation || { checks: [], all_passed: false };
  const checks = validation.checks || [];
  const passes = checks.filter((c) => c.status === "PASS").length;
  const fails = checks.filter((c) => c.status === "FAIL").length;
  const addr = caseData.communication?.registered_address;
  const status = caseData.case_meta?.status;
  const intakeComplete = caseData.handoff?.intake_complete;
  const pricing = caseData.pricing || {};

  return (
    <div className="h-full overflow-y-auto chat-scroll" data-testid="case-file-panel">
      <div className="p-6 sm:p-8">
        {/* Header */}
        <div className="pb-6 border-b border-slate-200 mb-6">
          <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500">
            Case file
          </div>
          <div className="flex items-baseline justify-between mt-2">
            <h2 className="font-display text-2xl text-slate-900 font-semibold" data-testid="case-id-header">
              {caseData.case_meta?.case_id}
            </h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
              {status || "DRAFT"}
            </span>
          </div>
        </div>

        {/* Phase tracker */}
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-3">
            Progress
          </div>
          <div className="relative pl-3">
            <div className="absolute left-[11px] top-2 bottom-2 w-px bg-slate-200" />
            {PHASES.map((p, i) => {
              const isDone = i < phaseIdx;
              const isActive = i === phaseIdx;
              return (
                <div key={p.id} className="flex items-center gap-3 mb-2.5 relative">
                  <div
                    className={[
                      "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold z-10 ring-4 ring-[#FFF8F1]",
                      isActive
                        ? "bg-[#C5283F] text-white"
                        : isDone
                        ? "bg-[#C5283F] text-white"
                        : "bg-white border border-slate-200 text-slate-400",
                    ].join(" ")}
                  >
                    {isDone ? "✓" : i + 1}
                  </div>
                  <span
                    className={[
                      "text-sm",
                      isActive ? "font-semibold text-slate-900" : "text-slate-500",
                    ].join(" ")}
                  >
                    {p.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Company */}
        <Section title="Company" icon={<Building2 size={14} />}>
          <Row label="Type" value={company.entity_type || "—"}
                testid="case-panel-entity-type" />
          <Row label="Industry" value={company.industry || "—"} />
          {(company.business_description || "").length > 0 && (
            <div className="text-xs text-slate-600 mt-2 leading-relaxed">
              {company.business_description.length > 140
                ? company.business_description.slice(0, 140) + "…"
                : company.business_description}
            </div>
          )}
        </Section>

        {/* Names */}
        <Section title={`Name preferences (${names.length}/2)`} icon={<FileText size={14} />}>
          {names.length === 0 && <Empty>No names proposed yet.</Empty>}
          {names.map((n, i) => (
            <div key={n.proposed_name || `name-${i}`} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <div className="text-sm text-slate-900 font-medium">{n.proposed_name}</div>
              <span
                className={[
                  "text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full",
                  PRECHECK_STYLES[n.precheck?.status || "NOT_CHECKED"],
                ].join(" ")}
                data-testid={`precheck-status-${i}`}
              >
                {(n.precheck?.status || "NOT_CHECKED").replace(/_/g, " ").toLowerCase()}
              </span>
            </div>
          ))}
        </Section>

        {/* Stakeholders */}
        <Section title={`Stakeholders (${stakeholders.length})`}>
          {stakeholders.length === 0 && <Empty>No directors/shareholders yet.</Empty>}
          {stakeholders.map((s) => {
            const name = [s.personal?.first_name, s.personal?.last_name].filter(Boolean).join(" ") || "Unnamed";
            return (
              <div key={s.stakeholder_id}
                   className="py-3 border-b border-slate-100 last:border-0"
                   data-testid={`stakeholder-row-${s.stakeholder_id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-900">{name}</div>
                  {s.user_confirmed ? (
                    <CheckCircle2 size={16} className="text-emerald-600" />
                  ) : (
                    <span className="text-xs text-amber-700">unconfirmed</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {(s.role || "").replace(/_/g, " ")}
                  {s.share_percent != null && <> · {s.share_percent}%</>}
                </div>
                <div className="text-[11px] font-mono text-slate-600 mt-1 flex items-center gap-2">
                  <Lock size={10} /> {maskAadhaar(s.identity?.aadhaar_last4)}
                </div>
              </div>
            );
          })}
        </Section>

        {/* Registered office */}
        <Section title="Registered office">
          {!addr || !addr.line1 ? (
            <Empty>Not provided yet.</Empty>
          ) : (
            <div className="text-sm text-slate-700 leading-relaxed">
              {addr.line1}, {addr.line2}
              <br />
              {addr.city && <>{addr.city}, </>}
              {addr.state} — {addr.pincode}
            </div>
          )}
        </Section>

        {/* Validation */}
        <Section title="Validation">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              {validation.all_passed ? (
                <CheckCircle2 size={16} className="text-emerald-600" />
              ) : fails > 0 ? (
                <AlertTriangle size={16} className="text-amber-600" />
              ) : (
                <XCircle size={16} className="text-slate-400" />
              )}
              <span className="text-slate-700" data-testid="validation-summary">
                {passes} of {checks.length || 21} checks passing
              </span>
            </div>
            {fails > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-200">
                {fails} fail
              </span>
            )}
          </div>
          {fails > 0 && (
            <ul className="mt-3 space-y-1.5">
              {checks.filter((c) => c.status === "FAIL").slice(0, 5).map((c, i) => (
                <li key={c.rule_id || c.message || `check-${i}`} className="text-xs text-red-800 leading-relaxed">
                  • {c.message || c.rule_id}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Pricing footer */}
        <div className="mt-8 pt-6 border-t border-slate-200">
          <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-3">
            Package
          </div>
          {(pricing.line_items || []).map((li, i) => (
            <div key={li.description || `line-${i}`} className="flex justify-between text-xs text-slate-600 py-1">
              <span>{li.description}</span>
              <span className="font-mono">₹{li.amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>
          ))}
          <div className="flex justify-between mt-3 pt-3 border-t border-slate-200">
            <span className="text-sm font-semibold text-slate-900">Total</span>
            <span className="text-sm font-semibold text-slate-900 flex items-center gap-0.5">
              <IndianRupee size={14} />
              {(pricing.display_total ?? 19999).toLocaleString("en-IN")}
            </span>
          </div>

          {intakeComplete && (
            <button
              data-testid="proceed-to-payment-btn"
              onClick={() => onPay?.(paymentUrl)}
              className="w-full mt-5 bg-[#C5283F] hover:bg-[#A82035] text-white px-6 py-3.5 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              <IndianRupee size={16} />
              Proceed to payment — ₹{(pricing.display_total ?? 19999).toLocaleString("en-IN")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="mb-7">
      <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-3 flex items-center gap-2 pb-2 border-b border-slate-200">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}
function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium" data-testid={testid}>{value}</span>
    </div>
  );
}
function Empty({ children }) {
  return <div className="text-xs text-slate-400 italic py-2">{children}</div>;
}
